// src/utils/taxCalculator.js
// Frontend mirror of calculator.py's tax math — NOT the generic gap-filler
// (that's the backend's job, filling genuine printed-bill gaps). This is
// the CA's live editing calculator: whichever field the CA just touched
// becomes the source of truth, and exactly the fields downstream of it
// (per the agreed hierarchy) recompute — never Qty, Discount, or Tax Rate,
// which are always manual-only anchors.

export function round2(n) {
  const v = parseFloat(n);
  return isNaN(v) ? 0 : Math.round(v * 100) / 100;
}

// ── Group A: Qty × Rate = Gross Taxable ──────────────────────────────────
// Qty is always manual — never recomputed. Rate and Gross are bidirectional.
export function recalcFromQtyOrRate(qty, rate) {
  const q = round2(qty);
  const r = round2(rate);
  return { qty: q, rate: r, gross: round2(q * r) };
}

export function recalcRateFromGross(gross, qty) {
  const q = round2(qty);
  if (!q) return 0;
  return round2(round2(gross) / q);
}

// ── Group B: Gross, Discount, NetTaxable, RatePct, Tax₹, Total ──────────
// Discount and RatePct are always manual anchors. Editing any ONE of
// {NetTaxable, Tax₹, Total} recomputes the other two, using the current
// (unchanged) Discount and RatePct.

export function netFromGross(gross, discount) {
  return round2(round2(gross) - round2(discount));
}

export function taxFromNet(net, ratePct) {
  return round2((round2(net) * round2(ratePct)) / 100);
}

export function totalFromNetAndTax(net, tax) {
  return round2(round2(net) + round2(tax));
}

export function netFromTaxAndRate(tax, ratePct) {
  const r = round2(ratePct);
  if (!r) return 0;
  return round2(round2(tax) / (r / 100));
}

export function netFromTotalAndTax(total, tax) {
  return round2(round2(total) - round2(tax));
}

export function taxFromTotalAndNet(total, net) {
  return round2(round2(total) - round2(net));
}

// ── Full recompute given "what just changed" ─────────────────────────────
// item shape: { qty, rate, gross, discount, net, ratePct, tax, total }
export function recalcLineItem(item, changedField, newValue) {
  let { qty, rate, gross, discount, net, ratePct, tax, total } = item;
  const val = parseFloat(newValue);
  const v = isNaN(val) ? 0 : val;

  switch (changedField) {
    case 'qty':
      qty = v;
      gross = round2(qty * rate);
      net = netFromGross(gross, discount);
      tax = taxFromNet(net, ratePct);
      total = totalFromNetAndTax(net, tax);
      break;
    case 'rate':
      rate = v;
      gross = round2(qty * rate);
      net = netFromGross(gross, discount);
      tax = taxFromNet(net, ratePct);
      total = totalFromNetAndTax(net, tax);
      break;
    case 'discount':
      discount = v;
      net = netFromGross(gross, discount);
      tax = taxFromNet(net, ratePct);
      total = totalFromNetAndTax(net, tax);
      rate = qty ? round2(gross / qty) : rate;
      break;
    case 'gross':
      gross = v;
      net = netFromGross(gross, discount);
      tax = taxFromNet(net, ratePct);
      total = totalFromNetAndTax(net, tax);
      rate = qty ? round2(gross / qty) : rate;
      break;
    case 'ratePct':
      ratePct = v;
      tax = taxFromNet(net, ratePct);
      total = totalFromNetAndTax(net, tax);
      break;
    case 'tax':
      tax = v;
      total = totalFromNetAndTax(net, tax);
      ratePct = net ? round2((tax / net) * 100) : ratePct;
      break;
    case 'total':
      total = v;
      net = ratePct ? round2(total / (1 + ratePct / 100)) : net;
      tax = taxFromTotalAndNet(total, net);
      gross = round2(net + discount);
      rate = qty ? round2(gross / qty) : rate;
      break;
    default:
      break;
  }

  return { qty, rate, gross, discount, net, ratePct, tax, total };
}

// ── Header: pure sums of line items + Round Off ──────────────────────────
export function recalcHeaderFromItems(lineItems, roundOff) {
  const grossSum = round2(lineItems.reduce((s, it) => s + (it.gross || 0), 0));
  const discSum  = round2(lineItems.reduce((s, it) => s + (it.discount || 0), 0));
  const netSum   = round2(lineItems.reduce((s, it) => s + (it.net || 0), 0));
  const taxSum   = round2(lineItems.reduce((s, it) => s + (it.tax || 0), 0));
  const totalSum = round2(netSum + taxSum + round2(roundOff || 0));
  return { grossSum, discSum, netSum, taxSum, totalSum };
}